/**
 * Session + QA overrides for local mock previews.
 * - Payment plan stepper: `test/design/paymentPlanFigmaQa.spec.js` — "Payment plan success receipt layout"
 * - Confirm and pay: `/payment-plan?confirmPreview=1`
 * - Payment success page: `PaymentSuccessPage` (`/payment-success`) — old vs splash vs frictionless layouts
 *
 * Used by `receiptPreviewBootstrap.js` when `?receiptPreview=1` or `?confirmPreview=1`
 * and `MOCK_API=true` at build time.
 */

import { PAYMENT_PLAN_TYPES } from '../constants';

const REF = "110398201";

export const receiptPreviewQaAccountOverride = {
  accountId: REF,
  referenceNumber: REF,
  accountholderFirstName: "Ricky",
  accountholderLastName: "Dunn",
  clientName: "Capital One Bank",
  currentCreditorName: "Spring Oaks Capital SPV, LLC",
  cardStoreBrand: "Walmart MC",
  remainingBalance: 2350.71,
  originalBalance: 2350.71,
  originalAccountNumber: "89526-Ciulla-2017",
  accountOpenedDate: "2016-01-15T00:00:00.000Z",
  geographicState: "CA",
  solDisclosureID: "12",
  fromShortLink: false,
  suppressed: false,
};

export const receiptPreviewQaPaymentPlansOverride = {
  accountId: REF,
  currentBalance: 2350.71,
  paymentPlans: [],
};

const receiptPreviewPaymentPlan = {
  planType: "paymentPartial",
  numPayments: 1,
  totalPaymentAmount: 300,
  standardPaymentAmount: 300,
  paymentStartDate: "2026-02-10T00:00:00.000Z",
  paymentSchedule: [
    {
      date: "2026-02-10T00:00:00.000Z",
      amount: 300,
    },
  ],
};

const receiptPreviewPaymentMethod = {
  consumerName: "A. User",
  paymentSource: "CHECKING",
  type: "ach",
  cardType: "",
  routingNumber: "061000104",
  pnRef: "pn-ref",
  result: "approved",
  last4: "7890",
  token: "mock-token",
  date: "2026-02-10T00:00:00.000Z",
  referenceNumber: REF,
};

/**
 * @returns {Record<string, unknown>} Full `soc-portal` sessionStorage payload
 */
export function getReceiptPreviewPortalState() {
  const expires = Date.now() + 8 * 60 * 60 * 1000;
  return {
    session: {
      authToken: "mock-token",
      expires,
      authenticated: true,
    },
    referenceNumbers: [REF],
    selectedReferenceNumber: REF,
    account: {
      ...receiptPreviewQaAccountOverride,
      accountId: REF,
      referenceNumber: REF,
    },
    redirectUrl: "",
    activePaymentPlans: receiptPreviewQaPaymentPlansOverride,
    paymentPlan: receiptPreviewPaymentPlan,
    paymentMethod: receiptPreviewPaymentMethod,
    selectedStep: "success",
  };
}

/**
 * @returns {Record<string, unknown>} Full `soc-portal` sessionStorage payload
 */
export function getConfirmPreviewPortalState() {
  const expires = Date.now() + 8 * 60 * 60 * 1000;
  return {
    session: {
      authToken: "mock-token",
      expires,
      authenticated: true,
    },
    referenceNumbers: [REF],
    selectedReferenceNumber: REF,
    account: {
      ...receiptPreviewQaAccountOverride,
      accountId: REF,
      referenceNumber: REF,
    },
    redirectUrl: "",
    activePaymentPlans: receiptPreviewQaPaymentPlansOverride,
    paymentPlan: {
      planType: PAYMENT_PLAN_TYPES.paymentPlan,
      numPayments: 6,
      totalPaymentAmount: 1620.76,
      standardPaymentAmount: 270.13,
      lastPaymentAmount: 270.11,
      paymentFrequency: "monthly",
      paymentDayOfMonth: 13,
      paymentStartDate: "2026-08-13T00:00:00.000Z",
      paymentSchedule: [
        { date: "2026-08-13T00:00:00.000Z", amount: 270.13 },
        { date: "2026-09-13T00:00:00.000Z", amount: 270.13 },
        { date: "2026-10-13T00:00:00.000Z", amount: 270.13 },
        { date: "2026-11-13T00:00:00.000Z", amount: 270.13 },
        { date: "2026-12-13T00:00:00.000Z", amount: 270.13 },
        { date: "2027-01-13T00:00:00.000Z", amount: 270.11 },
      ],
    },
    paymentMethod: receiptPreviewPaymentMethod,
    selectedStep: "confirm",
  };
}

/** @typedef {'old' | 'splash' | 'frictionless' | 'settlement'} PaymentSuccessPreviewVariant */

/**
 * State for `/payment-success` (PaymentSuccessPage). Route is not `requireAuth`, but the page
 * calls `logout()` if `paymentMethod.type` is missing, so the method must be present.
 *
 * - **old** — main portal layout (`oldSuccessPage`, GoBack + OldPaymentInfo)
 * - **splash** — PPLP-style (Header, confetti, `PaymentInfo`)
 * - **frictionless** — like splash but progress bar instead of step image
 * - **settlement** — settlement-offer success (`comesFromSplashPage`, `settleInFull` + settlement amounts)
 */
export function getPaymentSuccessPreviewPortalState(variant = "old") {
  const base = {
    ...getReceiptPreviewPortalState(),
    // Success route does not use payment-plan stepper selectedStep
    selectedStep: "",
  };

  if (variant === "settlement") {
    const offerAmount = 1200;
    const currentBalance = 2350.71;
    return {
      ...base,
      comesFromSplashPage: true,
      comesFromFrictionlessPage: false,
      defaultSpecialPlan: { planType: null },
      settlementOffers: {
        currentBalance,
        offerAmount,
        plans: [],
      },
      paymentPlan: {
        planType: PAYMENT_PLAN_TYPES.settleInFull,
        referenceNumber: REF,
        numPayments: 1,
        totalPaymentAmount: offerAmount,
        standardPaymentAmount: offerAmount,
        lastPaymentAmount: offerAmount,
        paymentFrequency: null,
        paymentDayOfWeek: null,
        paymentStartDate: "2026-02-10T00:00:00.000Z",
        paymentSchedule: [
          { date: "2026-02-10T00:00:00.000Z", amount: offerAmount },
        ],
      },
    };
  }

  const v = variant === "splash" || variant === "frictionless" ? variant : "old";

  const paymentPlan = {
    planType: PAYMENT_PLAN_TYPES.paymentPlan,
    referenceNumber: REF,
    numPayments: 3,
    totalPaymentAmount: 2350.71,
    standardPaymentAmount: 783.57,
    lastPaymentAmount: 783.57,
    paymentFrequency: "monthly",
    paymentDayOfWeek: null,
    paymentStartDate: "2026-02-10T00:00:00.000Z",
    paymentSchedule: [
      { date: "2026-02-10T00:00:00.000Z", amount: 783.57 },
      { date: "2026-03-10T00:00:00.000Z", amount: 783.57 },
      { date: "2026-04-10T00:00:00.000Z", amount: 783.57 },
    ],
  };

  return {
    ...base,
    comesFromSplashPage: v === "splash",
    comesFromFrictionlessPage: v === "frictionless",
    settlementOffers: {
      currentBalance: null,
      offerAmount: null,
      plans: [],
    },
    defaultSpecialPlan: { planType: null },
    paymentPlan,
  };
}
